import { ApplicantTabNavigation } from '@components/layout/ApplicantTabNavigation'
import { ApplicationHeader } from '@components/layout/ApplicationHeader'
import { getApplicationHeaderProps } from '@lib/utils/applicationHeaderHelpers'
import { useState, useEffect, useMemo } from 'react'
import { FileText, ArrowRight, X, Save, ChevronDown, Loader2, AlertTriangle, AlertCircle } from 'lucide-react'
import { useAppSelector, useAppDispatch } from '@/app/hooks'
import { useNavigate, useParams } from 'react-router-dom'
import { getTabPathWithApplicantId, getTabNavigationOptions } from '@lib/utils/applicationNavigation'
import { useTabValidation, useTabAutoSave } from "@/features/applicant/context/TabValidationContext";
import { APPLICANT_CONTENT } from '@/features/applicant/constants/applicantContent'
import { useGoogleTranslateRefresh } from '@/hooks/useGoogleTranslateRefresh'
import {
  selectApplicationData,
  selectApplicationLoading,
  selectIsReadOnly,
  updateApplication,
  fetchApplicationById,
  fetchApplications,
} from '@/features/applicant/applicationSlice'
import { residentTabValidation, type UpdateApplicationRequest } from '@/types/application'
import { DatePicker } from '@components/ui/DatePicker'
import { convertToDateInputFormat, convertToDisplayFormat } from '@lib/utils/dateValidation'

import { ValidationModal } from '@components/ui/ValidationModal'
import { applicantValidationModalProps } from '@/features/applicant/constants/applicantDialogProps'
import { sendAnalytics } from '@/lib/utils/analytics'
import { formatApiError, showError, showWarning } from '@/lib/utils/notifications'

/** API may return boolean, 1/0, or '1'/'0' for history flags. */
function isAffirmativeFlag(value: unknown): boolean {
  return value === true || value === '1' || value === 1
}

export function History() {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { applicantId: urlApplicantId } = useParams<{ applicantId?: string }>()
  const applicationData = useAppSelector(selectApplicationData)
  const isLoading = useAppSelector(selectApplicationLoading)
  const isReadOnly = useAppSelector(selectIsReadOnly)
  const { registerValidator, unregisterValidator } = useTabValidation();
  // Get applicant ID from URL or Redux
  const applicantId = urlApplicantId || applicationData?.applicant_id

  const [hasFetched, setHasFetched] = useState(false)
  const [hasBeenEvicted, setHasBeenEvicted] = useState<boolean | null>(null)
  const [isBankrupt, setIsBankrupt] = useState<boolean | null>(null)
  const [bankruptcyType, setBankruptcyType] = useState<string>('')
  const [isBankruptcySettled, setIsBankruptcySettled] = useState<boolean | null>(null) // true = settled, false = still open
  const [bankruptcyExplanation, setBankruptcyExplanation] = useState<string>('')
  const [bankruptcySettlementDate, setBankruptcySettlementDate] = useState<string>('') // Date when bankruptcy was settled

  // Re-apply Google Translate when Yes/No answers reveal extra fields.
  const googleTranslateContentKey = useMemo(
    () =>
      [
        String(hasBeenEvicted),
        String(isBankrupt),
        bankruptcyType,
        String(isBankruptcySettled),
      ].join('|'),
    [hasBeenEvicted, isBankrupt, bankruptcyType, isBankruptcySettled],
  )
  useGoogleTranslateRefresh(googleTranslateContentKey)
  const [numberOfDependents, setNumberOfDependents] = useState<string>('0')
  // checking / saving / creditcard: no controls on this tab — preserve server values on save (see performSave).
  const [isSaving, setIsSaving] = useState(false)
  const [isSavingExit, setIsSavingExit] = useState(false)
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null)
  // Field-level validation errors
  const [fieldErrors, setFieldErrors] = useState<{
    hasBeenEvicted?: string
    isBankrupt?: string
    bankruptcyType?: string
    isBankruptcySettled?: string
    bankruptcyExplanation?: string
    numberOfDependents?: string
  }>({})
  const [validateForm, setValidateForm] = useState(false)
  const [showValidationModal, setShowValidationModal] = useState(false)

  const [validationModalMessage, setValidationModalMessage] = useState('')

  // Convert MM/DD/YYYY to YYYY-MM-DD format for API (matching Angular implementation)
  const convertToAPIDateFormat = (dateStr: string): string => {
    if (!dateStr) return ''
    const parts = dateStr.split('/')
    if (parts.length === 3) {
      const [month, day, year] = parts
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
    }
    // If already in YYYY-MM-DD format, return as is
    if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return dateStr
    }
    return ''
  }

  // Scroll to top when component mounts (when user navigates to this page)
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
  }, [])

  // Fetch application data on mount if not already loaded
  useEffect(() => {
    if (!applicationData?.applicant_id && !isLoading && !hasFetched) {
      setHasFetched(true)
      // Try to get applicant_id from storage
      const storedApplicantId = localStorage.getItem('applicant_id')
      if (storedApplicantId) {
        dispatch(fetchApplicationById(storedApplicantId))
      } else {
        dispatch(fetchApplications())
      }
    }
  }, [applicationData?.applicant_id, isLoading, hasFetched, dispatch])

  // Fetch latest application data to update status on mount
  useEffect(() => {
    const currentApplicantId = applicationData?.applicant_id
    if (currentApplicantId) {
      // Fetch latest application data to get updated status
      dispatch(fetchApplicationById(currentApplicantId)).catch((error) => {
        showWarning(formatApiError(error))
      })
    }
  }, []) // Only run on mount

  // Load existing history data from application data
  useEffect(() => {
    if (!applicationData) return

    const appData = applicationData

    // Load eviction status - only set if value is explicitly provided (not null/undefined/empty)
    // This ensures buttons are not pre-selected by default
    // Check API field name first, then legacy field names
    if (appData.rent_debt_collection !== undefined && appData.rent_debt_collection !== null && appData.rent_debt_collection !== '') {
      const isEvicted = isAffirmativeFlag(appData.rent_debt_collection)
      setHasBeenEvicted(isEvicted)
    } else if (appData.evicted !== undefined && appData.evicted !== null && appData.evicted !== '') {
      const isEvicted = isAffirmativeFlag(appData.evicted)
      setHasBeenEvicted(isEvicted)
    } else if (appData.has_been_evicted !== undefined && appData.has_been_evicted !== null && appData.has_been_evicted !== '') {
      const isEvicted = isAffirmativeFlag(appData.has_been_evicted)
      setHasBeenEvicted(isEvicted)
    }
    // Otherwise keep as null (no button selected)

    // Load bankruptcy status - only set if value is explicitly provided
    // Check API field name first, then legacy field names
    // Explicitly handle "0" as false, "1" as true, null/undefined as null
    if (appData.involved_bankruptcy !== undefined && appData.involved_bankruptcy !== null) {
      if (appData.involved_bankruptcy === '0' || appData.involved_bankruptcy === 0) {
        setIsBankrupt(false)
      } else if (isAffirmativeFlag(appData.involved_bankruptcy)) {
        setIsBankrupt(true)
      }
    } else if (appData.bankruptcy !== undefined && appData.bankruptcy !== null) {
      if (appData.bankruptcy === '0' || appData.bankruptcy === 0) {
        setIsBankrupt(false)
      } else if (appData.bankruptcy === true || appData.bankruptcy === '1' || appData.bankruptcy === 1) {
        setIsBankrupt(true)
      }
    } else if (appData.is_bankrupt !== undefined && appData.is_bankrupt !== null) {
      if (appData.is_bankrupt === '0' || appData.is_bankrupt === 0) {
        setIsBankrupt(false)
      } else if (appData.is_bankrupt === true || appData.is_bankrupt === '1' || appData.is_bankrupt === 1) {
        setIsBankrupt(true)
      }
    }
    // Otherwise keep as null (no button selected) - don't set state if value is null/undefined

    // Load bankruptcy details - only if bankruptcy is explicitly true
    // Check both API field name and legacy field name (matching Angular implementation)
    const isBankruptFromData =
      isAffirmativeFlag(appData.involved_bankruptcy) ||
      isAffirmativeFlag(appData.bankruptcy)

    if (isBankruptFromData) {
      // Load bankruptcy type
      if (appData.bankruptcy_type) {
        setBankruptcyType(appData.bankruptcy_type)
      }

      // Load settlement status from the Angular field still_open_bankruptcy. Despite its name this
      // is the "Is it Settled?" answer: '1' = settled (Yes), '0' = not settled (No) — see
      // resident-application-add.component.html (radio Yes value="1" / No value="0").
      if (appData.still_open_bankruptcy !== undefined && appData.still_open_bankruptcy !== null && appData.still_open_bankruptcy !== '') {
        setIsBankruptcySettled(isAffirmativeFlag(appData.still_open_bankruptcy)) // '1' (Yes) → settled = true
      } else if (appData.bankruptcy_settled !== undefined && appData.bankruptcy_settled !== null && appData.bankruptcy_settled !== '') {
        // Fallback to legacy field
        const isSettled = appData.bankruptcy_settled === true || appData.bankruptcy_settled === '1' || appData.bankruptcy_settled === 1
        setIsBankruptcySettled(isSettled)
      }

      // Load bankruptcy explanation - check Angular field (involved_bankruptcy_list) first
      if (appData.involved_bankruptcy_list) {
        setBankruptcyExplanation(appData.involved_bankruptcy_list)
      } else if (appData.bankruptcy_explanation) {
        // Fallback to legacy field
        setBankruptcyExplanation(appData.bankruptcy_explanation)
      }

      // Load settlement date (only shown when settled)
      // Convert from API format (YYYY-MM-DD) to display format (MM/DD/YYYY) if needed
      if (appData.involved_bankruptcy_date_to_settle) {
        const settlementDate = appData.involved_bankruptcy_date_to_settle
        // If date is in YYYY-MM-DD format, convert to MM/DD/YYYY
        if (settlementDate.includes('-') && !settlementDate.includes('/')) {
          setBankruptcySettlementDate(convertToDisplayFormat(settlementDate.split(' ')[0]))
        } else {
          setBankruptcySettlementDate(settlementDate)
        }
      }
    }

    // Load dependents - only if value exists and is meaningful (not empty string)
    // Check API field name first, then legacy field names
    if (appData.children !== undefined && appData.children !== null && appData.children !== '') {
      setNumberOfDependents(String(appData.children))
    } else if (appData.number_of_dependents !== undefined && appData.number_of_dependents !== null && appData.number_of_dependents !== '') {
      setNumberOfDependents(String(appData.number_of_dependents))
    } else if (appData.dependents !== undefined && appData.dependents !== null && appData.dependents !== '') {
      setNumberOfDependents(String(appData.dependents))
    }
    // Otherwise keep default '0' value
  }, [applicationData])

  // Register validator for Tab Validation
  useEffect(() => {
    const validateHistoryTab = (): { isValid: boolean; message?: string } => {
      const errorMessages: string[] = [];

      if (hasBeenEvicted === null) {
        errorMessages.push("Please indicate if you have been evicted.");
      }

      if (isBankrupt === null) {
        errorMessages.push("Please indicate if you are involved in a bankruptcy.");
      } else if (isBankrupt === true) {
        if (!bankruptcyType) {
          errorMessages.push("Bankruptcy Type is required.");
        }
        if (isBankruptcySettled === null) {
          errorMessages.push("Please indicate if the bankruptcy is settled.");
        }
        if (!bankruptcyExplanation || !bankruptcyExplanation.trim()) {
          errorMessages.push("Bankruptcy Explanation is required.");
        }
      }

      if (!numberOfDependents || numberOfDependents === "") {
        errorMessages.push("Number of Dependents is required.");
      }

      if (errorMessages.length > 0) {
        // Angular `validateForm` questioninfo override: a single generic message, not a per-field list.
        return {
          isValid: false,
          message: "Please correct required fields",
        };
      }

      return { isValid: true };
    };

    registerValidator("history", validateHistoryTab);
    return () => unregisterValidator("history");
  }, [
    registerValidator,
    unregisterValidator,
    hasBeenEvicted,
    isBankrupt,
    bankruptcyType,
    isBankruptcySettled,
    bankruptcyExplanation,
    numberOfDependents,
  ]);

  // Clear bankruptcy fields when isBankrupt is set to false
  useEffect(() => {
    if (isBankrupt === false) {
      setBankruptcyType('')
      setIsBankruptcySettled(null)
      setBankruptcyExplanation('')
      setBankruptcySettlementDate('')
    }
  }, [isBankrupt])

  // Build the history tab's API payload from current form state.
  // Single source of truth for both the Save button and auto-save-on-tab-change.
  const buildHistoryUpdateData = (): UpdateApplicationRequest => {
    const snapshot = applicationData as Record<string, unknown>
    const creditcardPersist =
      snapshot.creditcard === '1' || snapshot.creditcard === 1 || snapshot.creditcard === true ? '1' : '0'
    const checkingPersist =
      snapshot.checking === '1' || snapshot.checking === 1 || snapshot.checking === true ? '1' : '0'
    const savingPersist =
      snapshot.saving === '1' || snapshot.saving === 1 || snapshot.saving === true ? '1' : '0'

    const updateData: UpdateApplicationRequest = {
      ...residentTabValidation('history'),
      rent_debt_collection: hasBeenEvicted ? '1' : '0',
      rent_supporting_document: '',
      checking: checkingPersist,
      saving: savingPersist,
      creditcard: creditcardPersist,
      involved_bankruptcy: isBankrupt ? '1' : '0',
      children: numberOfDependents || '0',
      enter_date: {},
      evicted: hasBeenEvicted ? '1' : '0',
      bankruptcy: isBankrupt ? '1' : '0',
      number_of_dependents: parseInt(numberOfDependents) || 0,
    }

    if (isBankrupt === true) {
      updateData.bankruptcy_type = bankruptcyType
      // Angular parity: still_open_bankruptcy is the "Is it Settled?" answer —
      // '1' = settled (Yes), '0' = not settled (No). (template radio Yes value="1" / No value="0")
      updateData.still_open_bankruptcy = isBankruptcySettled === true ? '1' : (isBankruptcySettled === false ? '0' : '')
      updateData.involved_bankruptcy_list = bankruptcyExplanation.trim()
      updateData.bankruptcy_settled = isBankruptcySettled ? '1' : '0'
      updateData.bankruptcy_explanation = bankruptcyExplanation.trim()
      if (isBankruptcySettled === true && bankruptcySettlementDate) {
        updateData.involved_bankruptcy_date_to_settle = convertToAPIDateFormat(bankruptcySettlementDate)
      }
    }

    return updateData
  }

  // Auto-save-on-tab-change (Angular submitForm('fromtab') parity): persist the
  // current tab WITHOUT navigating. Dirty-guarded by useTabAutoSave below.
  const autoSaveHistory = async (): Promise<void> => {
    if (!applicationData?.applicant_id) return
    await dispatch(updateApplication({
      applicantId: applicationData.applicant_id,
      updateData: buildHistoryUpdateData(),
    })).unwrap()
    await dispatch(fetchApplicationById(applicationData.applicant_id)).unwrap()
  }

  useTabAutoSave({
    name: 'history',
    snapshot: [
      hasBeenEvicted,
      isBankrupt,
      bankruptcyType,
      isBankruptcySettled,
      bankruptcyExplanation,
      bankruptcySettlementDate,
      numberOfDependents,
    ],
    loadedKey: applicationData?.applicant_id,
    save: autoSaveHistory,
    isReadOnly,
  })

  // Handle the actual save logic
  const performSave = async (exitAfterSave: boolean) => {
    if (!applicationData?.applicant_id) {
      showError('Application ID not found. Please refresh the page.')
      return
    }

    if (exitAfterSave) {
      setIsSavingExit(true)
    } else {
      setIsSaving(true)
    }
    setSaveMessage(null)

    try {
      const updateData = buildHistoryUpdateData()

      // Save to API
      await dispatch(updateApplication({
        applicantId: applicationData.applicant_id,
        updateData: updateData,
      })).unwrap()

      // Fetch updated data from API
      await dispatch(fetchApplicationById(applicationData.applicant_id)).unwrap()

      setSaveMessage({ type: 'success', text: 'History information saved successfully!' })
      sendAnalytics('autosave', 'resident application info', applicationData?.applicant_id)

      // Navigate to next page or dashboard after ensuring Redux state is updated
      setTimeout(() => {
        if (exitAfterSave) {
          navigate('/applicant/dashboard', { replace: true })
        } else {
          // Proceed to the next tab (Signature). Angular always advances on a successful save;
          // the old "stay on page when bankrupt" behavior went with the removed bankruptcy popups.
          const signaturePath = applicantId
            ? getTabPathWithApplicantId('signature', applicantId)
            : '/applicant/application/signature'
          if (applicantId) {
            navigate(signaturePath, { ...getTabNavigationOptions('signature'), replace: true })
          } else {
            navigate(signaturePath, { replace: true })
          }
        }
      }, 300)
    } catch (error) {
      sendAnalytics('autosave', 'resident application info error', applicationData?.applicant_id)
      // Surface the server response. The inline save banner only renders success messages,
      // so an error set there is invisible — use the error toast (matches Angular appService.notify
      // and the error-handling standard: extract from error.response.data, never swallow).
      showError(formatApiError(error))
    } finally {
      setIsSaving(false)
      setIsSavingExit(false)
    }
  }

  // Handle save and continue (or save and exit when exitAfterSave is true)
  const handleSaveAndContinue = async (exitAfterSave = false) => {
    // If read-only mode, just navigate to next section without saving
    if (isReadOnly) {
      const signaturePath = applicantId
        ? getTabPathWithApplicantId('signature', applicantId)
        : '/applicant/application/signature'
      if (applicantId) {
        navigate(signaturePath, getTabNavigationOptions('signature'))
      } else {
        navigate(signaturePath)
      }
      return
    }

    // Validate all fields
    const errors: typeof fieldErrors = {}

    // 1. Check if evicted question is answered
    if (hasBeenEvicted === null) {
      errors.hasBeenEvicted = APPLICANT_CONTENT.errors.history.hasBeenEvicted
    }

    // 2. Check if bankruptcy question is answered
    if (isBankrupt === null) {
      errors.isBankrupt = APPLICANT_CONTENT.errors.history.isBankrupt
    }

    // 3. Only validate bankruptcy details if APPLICANT_CONTENT.applyNow.step1.yesOption is selected
    if (isBankrupt === true) {
      if (!bankruptcyType) {
        errors.bankruptcyType = APPLICANT_CONTENT.errors.history.bankruptcyType
      }
      if (isBankruptcySettled === null) {
        errors.isBankruptcySettled = APPLICANT_CONTENT.errors.history.isBankruptcySettled
      }
      if (!bankruptcyExplanation.trim()) {
        errors.bankruptcyExplanation = APPLICANT_CONTENT.errors.history.bankruptcyExplanation
      }
    }

    if (!numberOfDependents || numberOfDependents === '') {
      errors.numberOfDependents = APPLICANT_CONTENT.errors.history.numberOfDependents
    }

    // If there are validation errors, set them and return.
    // Inline field errors still highlight each control (Angular marks them ng-invalid);
    // the popup message matches Angular `validateForm` questioninfo override: a single generic line.
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors)
      setValidateForm(true)
      setValidationModalMessage('Please correct required fields')
      setShowValidationModal(true)
      return
    }

    // Clear errors if validation passes
    setFieldErrors({})
    setValidateForm(false)

    // Proceed with save. Angular has no bankruptcy confirmation/blocking popups in this
    // flow (neither the "may impact eligibility" confirm nor the "we can not support open
    // bankruptcies" notice), so a Yes answer — settled or not — saves directly.
    performSave(exitAfterSave)
  }

  return (
    <div>
      {/* Header - Resident Application */}
      <ApplicationHeader
        {...getApplicationHeaderProps(applicationData, APPLICANT_CONTENT.application.status.incomplete)}
        balanceDue={applicationData?.balance_due ? Number(applicationData.balance_due) : undefined}
        paymentDueDate={applicationData?.payment_due_date}
        applicantStatus={applicationData?.applicant_status || applicationData?.application_status || ''}
      />

      {/* Tab Navigation */}
      <ApplicantTabNavigation />

      {/* Content - form-readonly when applicant_lock so header/stepper stay clickable (per Angular) */}
      <div className={applicationData?.applicant_lock === "1" ? "form-readonly" : undefined}>
      <div data-testid="history-page" className="p-3 sm:p-6 bg-gray-50 min-h-screen">
        <div className="bg-white rounded-xl shadow-md border border-gray-200 p-4 sm:p-6 md:p-8 mb-4 sm:mb-6">
          <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-200">
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
              <FileText className="w-5 h-5 text-amber-600" />
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-bold text-gray-900">
                {APPLICANT_CONTENT.application.steps.history.title}
              </h2>
            </div>
          </div>

          {/* Rental / Property Section */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Have you been evicted? <span className="text-red-500">*</span>
            </label>
            <div className="flex flex-wrap gap-4">
              <button
                type="button"
                data-testid="history-evicted-yes-btn"
                onClick={() => {
                  if (!isReadOnly) {
                    setHasBeenEvicted(true)
                    if (fieldErrors.hasBeenEvicted) {
                      setFieldErrors(prev => {
                        const newErrors = { ...prev }
                        delete newErrors.hasBeenEvicted
                        return newErrors
                      })
                    }
                  }
                }}
                disabled={isReadOnly}
                className={`px-6 py-2 border rounded-md transition-colors ${hasBeenEvicted === true
                  ? 'bg-[#1597ff] text-white border-[#1597ff]'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                Yes
              </button>
              <button
                type="button"
                data-testid="history-evicted-no-btn"
                onClick={() => {
                  if (!isReadOnly) {
                    setHasBeenEvicted(false)
                    if (fieldErrors.hasBeenEvicted) {
                      setFieldErrors(prev => {
                        const newErrors = { ...prev }
                        delete newErrors.hasBeenEvicted
                        return newErrors
                      })
                    }
                  }
                }}
                disabled={isReadOnly}
                className={`px-6 py-2 border rounded-md transition-colors ${hasBeenEvicted === false
                  ? 'bg-[#1597ff] text-white border-[#1597ff]'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                No
              </button>
            </div>
            {validateForm && fieldErrors.hasBeenEvicted && (
              <div className="mt-1 text-sm text-red-600">
                <AlertCircle className="inline-block w-4 h-4 mr-2" />
                {fieldErrors.hasBeenEvicted}
              </div>
            )}
          </div>

          {/* Financial Section */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Are you currently involved in a bankruptcy? <span className="text-red-500">*</span>
            </label>
            <div className="flex flex-wrap gap-4">
              <button
                type="button"
                data-testid="history-bankrupt-yes-btn"
                onClick={() => {
                  if (!isReadOnly) {
                    setIsBankrupt(true)
                    // Don't show modal immediately - wait for save
                    if (fieldErrors.isBankrupt) {
                      setFieldErrors(prev => {
                        const newErrors = { ...prev }
                        delete newErrors.isBankrupt
                        return newErrors
                      })
                    }
                  }
                }}
                disabled={isReadOnly}
                className={`px-6 py-2 border rounded-md transition-colors ${isBankrupt === true
                  ? 'bg-[#1597ff] text-white border-[#1597ff]'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                Yes
              </button>
              <button
                type="button"
                data-testid="history-bankrupt-no-btn"
                onClick={() => {
                  if (!isReadOnly) {
                    setIsBankrupt(false)
                    setBankruptcyType('')
                    setIsBankruptcySettled(null)
                    setBankruptcyExplanation('')
                    setBankruptcySettlementDate('')
                    // Clear bankruptcy-related errors
                    setFieldErrors(prev => {
                      const newErrors = { ...prev }
                      delete newErrors.isBankrupt
                      delete newErrors.bankruptcyType
                      delete newErrors.isBankruptcySettled
                      delete newErrors.bankruptcyExplanation
                      return newErrors
                    })
                  }
                }}
                disabled={isReadOnly}
                className={`px-6 py-2 border rounded-md transition-colors ${isBankrupt === false
                  ? 'bg-[#1597ff] text-white border-[#1597ff]'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                No
              </button>
            </div>
            {validateForm && fieldErrors.isBankrupt && (
              <div className="mt-1 text-sm text-red-600">
                <AlertCircle className="inline-block w-4 h-4 mr-2" />
                {fieldErrors.isBankrupt}
              </div>
            )}

            {/* Conditional Fields - Show ONLY when Yes is selected */}
            {isBankrupt === true && (
              <div className="mt-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    What type? <span className="text-red-500">*</span>
                  </label>
                  <div className="relative max-w-md">
                    <select
                      data-testid="history-bankruptcy-type-select"
                      value={bankruptcyType}
                      onChange={(e) => {
                        if (!isReadOnly) {
                          setBankruptcyType(e.target.value)
                          if (fieldErrors.bankruptcyType) {
                            setFieldErrors(prev => {
                              const newErrors = { ...prev }
                              delete newErrors.bankruptcyType
                              return newErrors
                            })
                          }
                        }
                      }}
                      disabled={isReadOnly}
                      className={`w-full px-3 py-2 border rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none cursor-pointer disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed ${validateForm && fieldErrors.bankruptcyType ? 'border-red-500 focus:ring-red-500' : 'border-gray-300'
                        }`}
                    >
                      <option value=""></option>
                      <option value="chapter7">Chapter 7</option>
                      <option value="chapter13">Chapter 13</option>
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
                  </div>
                  {validateForm && fieldErrors.bankruptcyType && (
                    <div className="mt-1 text-sm text-red-600">
                      <AlertCircle className="inline-block w-4 h-4 mr-2" />
                      {fieldErrors.bankruptcyType}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Is it Settled? <span className="text-red-500">*</span>
                  </label>
                  <div className="flex flex-wrap gap-4 max-w-xs">
                    <button
                      type="button"
                      data-testid="history-bankruptcy-settled-yes-btn"
                      onClick={() => {
                        if (!isReadOnly) {
                          setIsBankruptcySettled(true)
                          if (fieldErrors.isBankruptcySettled) {
                            setFieldErrors(prev => {
                              const newErrors = { ...prev }
                              delete newErrors.isBankruptcySettled
                              return newErrors
                            })
                          }
                        }
                      }}
                      disabled={isReadOnly}
                      className={`px-6 py-2 border rounded-md transition-colors ${isBankruptcySettled === true
                        ? 'bg-[#1597ff] text-white border-[#1597ff]'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      Yes
                    </button>
                    <button
                      type="button"
                      data-testid="history-bankruptcy-settled-no-btn"
                      onClick={() => {
                        if (!isReadOnly) {
                          setIsBankruptcySettled(false)
                          setBankruptcySettlementDate('') // Clear date when changed to APPLICANT_CONTENT.applyNow.step1.noOption (still open)
                          if (fieldErrors.isBankruptcySettled) {
                            setFieldErrors(prev => {
                              const newErrors = { ...prev }
                              delete newErrors.isBankruptcySettled
                              return newErrors
                            })
                          }
                        }
                      }}
                      disabled={isReadOnly}
                      className={`px-6 py-2 border rounded-md transition-colors ${isBankruptcySettled === false
                        ? 'bg-[#1597ff] text-white border-[#1597ff]'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      No
                    </button>
                  </div>
                  {validateForm && fieldErrors.isBankruptcySettled && (
                    <div className="mt-1 text-sm text-red-600">
                      <AlertCircle className="inline-block w-4 h-4 mr-2" />
                      {fieldErrors.isBankruptcySettled}
                    </div>
                  )}
                  {/* Show settlement date field when APPLICANT_CONTENT.applyNow.step1.yesOption (settled) is selected - matching Angular */}
                  {isBankruptcySettled === true && (
                    <div className="mt-4 max-w-xs">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Date to be Settled? <span className="text-red-500">*</span>
                      </label>
                      <DatePicker
                        value={convertToDateInputFormat(bankruptcySettlementDate)}
                        onChange={(value) => {
                          if (!isReadOnly) {
                            const displayValue = convertToDisplayFormat(value)
                            setBankruptcySettlementDate(displayValue)
                          }
                        }}
                        placeholder={APPLICANT_CONTENT.application.sections.personalProfile.fields.placeholder.dateOfBirth}
                        allowFutureDates={true}
                        showHelperText={false}
                      />
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Please explain <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    data-testid="history-bankruptcy-explanation-textarea"
                    value={bankruptcyExplanation}
                    onChange={(e) => {
                      if (!isReadOnly) {
                        setBankruptcyExplanation(e.target.value)
                        if (fieldErrors.bankruptcyExplanation) {
                          setFieldErrors(prev => {
                            const newErrors = { ...prev }
                            delete newErrors.bankruptcyExplanation
                            return newErrors
                          })
                        }
                      }
                    }}
                    disabled={isReadOnly}
                    placeholder="Provide details about your bankruptcy situation..."
                    rows={4}
                    className={`w-full max-w-2xl px-3 py-2 border rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed ${validateForm && fieldErrors.bankruptcyExplanation ? 'border-red-500 focus:ring-red-500' : 'border-gray-300'
                      }`}
                  />
                  {validateForm && fieldErrors.bankruptcyExplanation && (
                    <div className="mt-1 text-sm text-red-600">
                      <AlertCircle className="inline-block w-4 h-4 mr-2" />
                      {fieldErrors.bankruptcyExplanation}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Family Section */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              How many children (or other dependents) will reside with you? <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              data-testid="history-dependents-input"
              value={numberOfDependents}
              onChange={(e) => {
                if (!isReadOnly) {
                  const value = e.target.value

                  if (value === '') {
                    setNumberOfDependents('')
                    // Clear error when field is modified
                    if (fieldErrors.numberOfDependents) {
                      setFieldErrors(prev => {
                        const newErrors = { ...prev }
                        delete newErrors.numberOfDependents
                        return newErrors
                      })
                    }
                  } else if (value.match(/^\d+$/)) {
                    const intValue = parseInt(value, 10)
                    if (intValue >= 0 && intValue <= 99) {
                      setNumberOfDependents(intValue.toString())

                      // Clear error when field is modified
                      if (fieldErrors.numberOfDependents) {
                        setFieldErrors(prev => {
                          const newErrors = { ...prev }
                          delete newErrors.numberOfDependents
                          return newErrors
                        })
                      }
                    }
                  }
                }
              }}
              disabled={isReadOnly}
              min="0"
              max="99"
              maxLength={2}
              className={`w-full max-w-xs px-3 py-2 border rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed ${validateForm && fieldErrors.numberOfDependents ? 'border-red-500 focus:ring-red-500' : 'border-gray-300'
                }`}
              placeholder="0"
            />
            {validateForm && fieldErrors.numberOfDependents && (
              <div className="mt-1 text-sm text-red-600">
                <AlertCircle className="inline-block w-4 h-4 mr-2" />
                {fieldErrors.numberOfDependents}
              </div>
            )}
          </div>

          {/* Save Message - Only show success messages */}
          {saveMessage && saveMessage.type === 'success' && (
            <div className="mb-4 p-4 rounded-lg flex items-start gap-3 bg-green-50 border border-green-200">
              <AlertTriangle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-green-800">
                  {saveMessage.text}
                </p>
              </div>
              <button
                onClick={() => setSaveMessage(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-3 pt-4 pb-6">
          <button
            data-testid="history-back-btn"
            type="button"
            onClick={() => {
              const incomePath = applicantId
                ? getTabPathWithApplicantId('income', applicantId)
                : '/applicant/application/income'
              if (applicantId) {
                navigate(incomePath, getTabNavigationOptions('income'))
              } else {
                navigate(incomePath)
              }
            }}
            disabled={isSaving || isSavingExit}
            className="inline-flex items-center gap-2 bg-[#1597ff] hover:bg-[#0d7acc] text-white font-medium px-8 py-3 rounded-lg transition-all shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {APPLICANT_CONTENT.application.buttons.back}
          </button>
          {!isReadOnly && (
            <button
              data-testid="history-save-continue-btn"
              onClick={() => handleSaveAndContinue()}
              disabled={
                isSaving || isSavingExit || !applicationData?.applicant_id
              }
              className="inline-flex items-center gap-2 bg-[#1597ff] hover:bg-[#0d7acc] text-white font-medium px-8 py-3 rounded-lg transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  {APPLICANT_CONTENT.application.buttons.saveContinue}
                </>
              )}
            </button>
          )}
          {isReadOnly && (
            <button
              data-testid="history-next-btn"
              onClick={() => {
                const signaturePath = applicantId
                  ? getTabPathWithApplicantId('signature', applicantId)
                  : '/applicant/application/signature'
                if (applicantId) {
                  navigate(signaturePath, getTabNavigationOptions('signature'))
                } else {
                  navigate(signaturePath)
                }
              }}
              className="inline-flex items-center gap-2 bg-[#1597ff] hover:bg-[#0d7acc] text-white font-medium px-8 py-3 rounded-lg transition-all shadow-md hover:shadow-lg"
            >
              {APPLICANT_CONTENT.applyNow.step3.nextButton}
              <ArrowRight className="w-4 h-4" />
            </button>
          )}
          <button
            data-testid="history-save-exit-btn"
            onClick={() => {
              if (isReadOnly) {
                navigate('/applicant/dashboard', { replace: true })
              } else {
                handleSaveAndContinue(true)
              }
            }}
            disabled={
              !isReadOnly &&
              (isSavingExit || isSaving || !applicationData?.applicant_id)
            }
            className="inline-flex items-center gap-2 bg-[#1597ff] hover:bg-[#0d7acc] text-white font-medium px-8 py-3 rounded-lg transition-all shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSavingExit ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : (
              isReadOnly ? APPLICANT_CONTENT.application.buttons.exit : APPLICANT_CONTENT.application.buttons.saveExit
            )}
          </button>
        </div>
      </div>

      {/* Validation Modal */}
      <ValidationModal
        {...applicantValidationModalProps}
        isOpen={showValidationModal}
        title={APPLICANT_CONTENT.errors.validationMessage}
        message={validationModalMessage}
        onClose={() => setShowValidationModal(false)}
      />
      </div>
    </div>
  )
}
